<template>
  <div ref="root" class="user-menu">
    <button
      class="user-menu__trigger"
      type="button"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="menu"
      :aria-label="t('navigation.account')"
      @click="open = !open"
    >
      <span class="user-menu__avatar" aria-hidden="true">{{ initial }}</span>
      <span class="user-menu__label">{{ label }}</span>
    </button>

    <ul v-if="open" class="user-menu__list" role="menu">
      <li role="none">
        <router-link
          class="user-menu__item"
          role="menuitem"
          :to="{ name: 'PlayerHomeDashboard' }"
          @click="open = false"
        >{{ t('navigation.dashboard') }}</router-link>
      </li>
      <li v-if="isGameMaster" role="none">
        <router-link
          class="user-menu__item"
          role="menuitem"
          :to="{ name: 'GameMaster' }"
          @click="open = false"
        >{{ t('navigation.gameMaster') }}</router-link>
      </li>
      <li role="none">
        <button class="user-menu__item" role="menuitem" type="button" @click="logout">
          {{ t('navigation.logout') }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ClientManagerService from '@/services/clientManagerService.js';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const open = ref(false);
const identity = ref(null);
const root = ref(null);

const isGameMaster = computed(() => store.getters.isGameMaster);

/*
 * While the identity request is in flight `identity` is null and the trigger
 * falls back to the generic account label. The menu is fully usable in that
 * state — dashboard and logout do not depend on the response. The name fields
 * are read optionally because the current serializer does not expose them yet.
 */
const label = computed(
  () => identity.value?.first_name || identity.value?.email || t('navigation.account')
);
const initial = computed(() => (label.value || '?').trim().charAt(0).toUpperCase());

const loadIdentity = async () => {
  if (!store.getters.isAuthenticated) {
    identity.value = null;
    return;
  }
  try {
    identity.value = await ClientManagerService.getCurrentClientInfo();
  } catch (error) {
    identity.value = null;
  }
};

const logout = () => {
  open.value = false;
  ClientManagerService.clearClientInfoCache();
  store.dispatch('logout');
  identity.value = null;
  router.push({ name: 'Home' });
};

const onDocumentClick = (event) => {
  if (open.value && root.value && !root.value.contains(event.target)) open.value = false;
};
const onKeydown = (event) => {
  if (event.key === 'Escape') open.value = false;
};

watch(() => route.fullPath, () => { open.value = false; });
watch(() => store.getters.isAuthenticated, loadIdentity);

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
  loadIdentity();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.user-menu {
  position: relative;
}

.user-menu__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 34px;
  padding: 0 var(--space-3);
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  color: var(--text-muted);
  font-family: var(--font-body);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  cursor: pointer;
  transition: color var(--duration) var(--ease), border-color var(--duration) var(--ease);
}

.user-menu__trigger:hover {
  color: var(--text);
  border-color: var(--border-accent);
}

.user-menu__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex: none;
  border-radius: var(--radius-pill);
  background: var(--accent-wash);
  color: var(--accent);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
}

/*
 * The label's width is reserved so the trigger does not resize when the
 * identity request lands and the generic label is replaced by a name.
 */
.user-menu__label {
  min-width: 6ch;
  max-width: 16ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-menu__list {
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  z-index: var(--z-overlay);
  min-width: 12rem;
  margin: 0;
  padding: var(--space-1);
  list-style: none;
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
}

.user-menu__list li {
  margin: 0;
}

.user-menu__item {
  display: block;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-muted);
  font-family: var(--font-body);
  font-size: var(--text-sm);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: color var(--duration) var(--ease), background-color var(--duration) var(--ease);
}

.user-menu__item:hover {
  background: var(--accent-wash);
  color: var(--text);
  text-decoration: none;
}
</style>
